import React from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import { lookupsJson } from '@ourbranch/lookups';
import { brandConfig } from 'brand-config';

import {
  requiredString,
  requiredIfSectionTownshipRange,
  requiredIfLotBlock,
  isCountyRequired,
  requiredIfStreetAddress
} from 'common/helpers/yup-helpers';
import Field from 'core/components/form/form.v2';
import CountiesAutocomplete from 'common/components/counties-autocomplete';
import AddressAutocomplete from 'core/components/address-autocomplete/address-autocomplete';

const { coordinateOptions } = lookupsJson;
const { legalDescriptionFormatOptions } = lookupsJson.farm;

Yup.addMethod(Yup.string, 'requiredIfSectionTownshipRange', requiredIfSectionTownshipRange);
Yup.addMethod(Yup.string, 'requiredIfLotBlock', requiredIfLotBlock);
Yup.addMethod(Yup.string, 'isCountyRequired', isCountyRequired);
Yup.addMethod(Yup.string, 'requiredIfStreetAddress', requiredIfStreetAddress);
Yup.addMethod(Yup.string, 'requiredString', requiredString);

const formatPath = 'legalDescription.format';

export const locationInitialValues = {
  legalDescription: {
    format: undefined,
    section: undefined,
    township: undefined,
    townshipCoordinates: undefined,
    range: undefined,
    rangeCoordinates: undefined,
    lot: undefined,
    block: undefined
  },
  description: '',
  numberOfAcres: undefined,
  address: {
    address: undefined,
    address2: undefined,
    city: undefined,
    state: undefined,
    zip: undefined,
    county: undefined
  },
  additionalLegalDescription: undefined,
  locationDirections: undefined
};

export const locationValidationSchema = Yup.object().shape({
  legalDescription: Yup.object().shape({
    format: Yup.string().requiredString('Legal Description Format is required'),
    lot: Yup.string().requiredIfLotBlock('Lot is required', formatPath),
    block: Yup.string().requiredIfLotBlock('Block is required', formatPath),
    section: Yup.string().requiredIfSectionTownshipRange('Section is required', formatPath),
    township: Yup.string().requiredIfSectionTownshipRange('Township is required', formatPath),
    range: Yup.string().requiredIfSectionTownshipRange('Range is required', formatPath),
    townshipCoordinates: Yup.string(),
    rangeCoordinates: Yup.string()
  }),
  address: Yup.object().shape({
    address: Yup.string().requiredIfStreetAddress('Address is required', formatPath),
    address2: Yup.string().nullable(),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zip: Yup.string().required('ZIP is required'),
    county: Yup.string().isCountyRequired('County is required', formatPath)
  }),
  description: Yup.string().required('Location name is required'),
  numberOfAcres: Yup.number().required('Acreage is required').integer('Must be a whole number')
});

const NotOnPolicyLocation = ({ disabled }) => {
  const { values, setFieldValue } = useFormikContext();

  const legalDescriptionFormat = values?.legalDescription?.format;
  const farmState = values?.address?.state || Object.keys(brandConfig.farm || {})[0] || 'IL';

  return (
    <>
      <Field
        xs={4}
        type="select"
        name="legalDescription.format"
        mode="dark"
        label="Legal description format*"
        options={legalDescriptionFormatOptions}
        onChange={(value) => {
          if (value === 'section-township-range') {
            setFieldValue('legalDescription.townshipCoordinates', 'unknown');
            setFieldValue('legalDescription.rangeCoordinates', 'unknown');
          }
        }}
        disabled={disabled}
      />
      <Field xs={4} type="numeric" name="numberOfAcres" mode="dark" label="Acres*" disabled={disabled} />
      <Field xs={4} type="string" name="description" mode="dark" label="Location name*" disabled={disabled} />

      {legalDescriptionFormat === 'street' && (
        <>
          <AddressAutocomplete
            id="address.address"
            name="address.address"
            xs={6}
            mode="dark"
            label="Address*"
            disabled={disabled}
          />
          <Field
            xs={6}
            type="string"
            name="address.address2"
            mode="dark"
            label="Apt, unit, building, etc."
            disabled={disabled}
          />
          <Field xs={4} type="string" name="address.city" mode="dark" label="City*" disabled={disabled} />
          <Field xs={4} type="state" name="address.state" mode="dark" label="State*" disabled={disabled} />
          <Field xs={4} type="string" name="address.zip" mode="dark" label="Zip code*" disabled={disabled} />
        </>
      )}

      {legalDescriptionFormat === 'section-township-range' && (
        <>
          <Field xs={4} type="string" name="address.city" mode="dark" label="City*" disabled={disabled} />
          <Field xs={4} type="state" name="address.state" mode="dark" label="State*" disabled={disabled} />
          <Field xs={4} type="string" name="address.zip" mode="dark" label="Zip code*" disabled={disabled} />
          <CountiesAutocomplete
            id="address.county"
            state={farmState}
            setValues={setFieldValue}
            mode="dark"
            disabled={disabled}
          />
          <Field
            xs={4}
            type="string"
            name="legalDescription.section"
            mode="dark"
            label="Section*"
            disabled={disabled}
          />
          <Field
            xs={2}
            type="string"
            name="legalDescription.township"
            mode="dark"
            label="Township*"
            disabled={disabled}
          />
          <Field
            xs={2}
            type="select"
            name="legalDescription.townshipCoordinates"
            mode="dark"
            label="Coordinates"
            options={coordinateOptions}
            disabled={disabled}
          />
          <Field xs={2} type="string" name="legalDescription.range" mode="dark" label="Range*" disabled={disabled} />
          <Field
            xs={2}
            type="select"
            name="legalDescription.rangeCoordinates"
            mode="dark"
            label="Coordinates"
            options={coordinateOptions}
            disabled={disabled}
          />
        </>
      )}

      {legalDescriptionFormat === 'lot-block' && (
        <>
          <Field xs={6} type="numeric" name="legalDescription.lot" mode="dark" label="Lot*" disabled={disabled} />
          <Field xs={6} type="numeric" name="legalDescription.block" mode="dark" label="Block*" disabled={disabled} />
          <Field xs={4} type="string" name="address.city" mode="dark" label="City*" disabled={disabled} />
          <Field xs={4} type="state" name="address.state" mode="dark" label="State*" disabled={disabled} />
          <Field xs={4} type="string" name="address.zip" mode="dark" label="Zip code*" disabled={disabled} />
          <CountiesAutocomplete
            id="address.county"
            state={farmState}
            setValues={setFieldValue}
            mode="dark"
            disabled={disabled}
          />
        </>
      )}

      {legalDescriptionFormat === 'long-legal' && (
        <>
          <Field xs={4} type="string" name="address.city" mode="dark" label="City*" disabled={disabled} />
          <Field xs={4} type="state" name="address.state" mode="dark" label="State*" disabled={disabled} />
          <Field xs={4} type="string" name="address.zip" mode="dark" label="Zip code*" disabled={disabled} />
          <CountiesAutocomplete
            id="address.county"
            state={farmState}
            setValues={setFieldValue}
            mode="dark"
            disabled={disabled}
          />
        </>
      )}

      {!!legalDescriptionFormat && (
        <>
          <Field
            xs={12}
            type="string"
            name="additionalLegalDescription"
            mode="dark"
            label="Additional legal description (optional)"
            disabled={disabled}
          />
          <Field
            xs={12}
            type="string"
            name="locationDirections"
            mode="dark"
            label="Location directions (optional)"
            disabled={disabled}
          />
        </>
      )}
    </>
  );
};

NotOnPolicyLocation.propTypes = {
  disabled: PropTypes.bool.isRequired
};

export default NotOnPolicyLocation;
